/**
 * B2B Volume Discount Properties Handler
 *
 * 1. Updates existing cart items with B2B catalog tiers when user logs in
 * 2. Handles guest-to-B2B login scenario
 *
 * NO FALLBACKS - must get actual B2B catalog tiers from Shopify
 */

(function() {
  'use strict';

  /**
   * Updates existing cart items with B2B catalog tiers
   * Called on page load to handle guest-to-B2B login scenario
   */
  async function updateExistingCartItems() {
    try {
      // Force fresh cart fetch (bypass browser cache)
      const cartResponse = await fetch('/cart.js', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      const cart = await cartResponse.json();

      if (!cart.items || cart.items.length === 0) return;

      // Find items missing _volume_price_breaks (the catalog tiers)
      const itemsNeedingUpdate = cart.items.filter(function(item) {
        return !item.properties || !item.properties._volume_price_breaks;
      });

      if (itemsNeedingUpdate.length === 0) {
        console.log('[B2B Volume Discount] All cart items have catalog tiers');
        return;
      }

      console.log(
        '[B2B Volume Discount] ' + itemsNeedingUpdate.length + ' items need catalog tier update'
      );

      // Fetch B2B catalog data for each variant
      const variantB2BData = new Map();

      await Promise.all(
        itemsNeedingUpdate.map(async function(item) {
          const data = await fetchVariantB2BData(item.handle, item.variant_id);
          if (data) {
            variantB2BData.set(item.variant_id, data);
          }
        })
      );

      // Build cart update payload
      const updates = {};
      let hasUpdates = false;

      for (let i = 0; i < itemsNeedingUpdate.length; i++) {
        const item = itemsNeedingUpdate[i];
        const b2bData = variantB2BData.get(item.variant_id);

        // Skip if no B2B data found (user might not be B2B)
        if (!b2bData) {
          console.log(
            '[B2B Volume Discount] No B2B catalog data for variant ' + item.variant_id
          );
          continue;
        }

        // Skip if no volume price breaks in catalog
        if (!b2bData.volumePriceBreaks) {
          console.log(
            '[B2B Volume Discount] No volume tiers in catalog for variant ' + item.variant_id
          );
          continue;
        }

        const newProperties = Object.assign({}, item.properties || {}, {
          _parent_product_id: b2bData.productId,
          _volume_price_breaks: b2bData.volumePriceBreaks,
        });

        updates[item.key] = {
          quantity: item.quantity,
          properties: newProperties,
        };
        hasUpdates = true;

        console.log(
          '[B2B Volume Discount] Will update variant ' + item.variant_id + ' with catalog tiers:',
          b2bData.volumePriceBreaks
        );
      }

      if (!hasUpdates) {
        console.log('[B2B Volume Discount] No catalog tier updates needed');
        return;
      }

      // Update cart items
      await updateCartItems(updates);

      console.log(
        '[B2B Volume Discount] Successfully updated cart with B2B catalog tiers'
      );

      // Refresh cart UI without full page reload
      await refreshCart();
    } catch (error) {
      console.error('[B2B Volume Discount] Failed to update cart items:', error);
    }
  }

  /**
   * Fetches B2B catalog data for a specific variant by loading its product page
   * The product page renders with B2B context if user is logged in
   */
  async function fetchVariantB2BData(handle, variantId) {
    try {
      // Fetch product page with variant param and cache bypass
      const response = await fetch(
        '/products/' + handle + '?variant=' + variantId + '&_=' + Date.now(),
        { cache: 'no-store' }
      );
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const productIdInput = doc.querySelector('input[name="properties[_parent_product_id]"]');
      const volumeBreaksInput = doc.querySelector('input[name="properties[_volume_price_breaks]"]');

      console.log('[B2B Volume Discount] Fetched variant ' + variantId + ':', {
        hasProductId: !!(productIdInput && productIdInput.value),
        hasVolumeBreaks: !!(volumeBreaksInput && volumeBreaksInput.value),
        volumeBreaks: (volumeBreaksInput && volumeBreaksInput.value) || 'none',
      });

      if (!productIdInput || !productIdInput.value) return null;

      return {
        productId: productIdInput.value,
        volumePriceBreaks: (volumeBreaksInput && volumeBreaksInput.value) || null,
      };
    } catch (error) {
      console.error(
        '[B2B Volume Discount] Failed to fetch B2B data for ' + handle + ':',
        error
      );
      return null;
    }
  }

  /**
   * Updates cart items via Shopify AJAX API
   */
  async function updateCartItems(updates) {
    // Shopify's /cart/change.js only handles one item at a time
    // Process updates sequentially to avoid race conditions
    const keys = Object.keys(updates);
    for (let i = 0; i < keys.length; i++) {
      const lineKey = keys[i];
      const updateData = updates[lineKey];
      
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: updateData.quantity,
          properties: updateData.properties,
        }),
      });
    }
  }

  /**
   * Refreshes the cart UI after updating line item properties
   */
  async function refreshCart() {
    try {
      // Fetch fresh cart data
      const cartResponse = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await cartResponse.json();

      // Update global cart data if it exists
      if (window.Cart && window.Cart.data) {
        window.Cart.data = cart;
      }

      // Dispatch common cart update events that theme components may listen to
      var cartEvents = [
        'cart:refresh',
        'cart:updated',
        'theme:cart:refresh',
        'cart:change',
      ];

      for (var i = 0; i < cartEvents.length; i++) {
        document.dispatchEvent(
          new CustomEvent(cartEvents[i], {
            bubbles: true,
            detail: { cart: cart },
          })
        );
      }

      // Also dispatch on window for components that listen there
      window.dispatchEvent(
        new CustomEvent('cart:refresh', {
          detail: { cart: cart },
        })
      );

      // Try to refresh cart sections via Section Rendering API
      await refreshCartSections();

      console.log('[B2B Volume Discount] Cart refreshed');
    } catch (error) {
      console.error('[B2B Volume Discount] Failed to refresh cart:', error);
      // Fallback to page reload if cart refresh fails
      window.location.reload();
    }
  }

  /**
   * Refreshes cart sections using Shopify's Section Rendering API
   */
  async function refreshCartSections() {
    try {
      // Common cart section IDs - fetch all that might exist
      var sectionIds = [
        'cart-drawer',
        'cart-items',
        'cart-icon-bubble',
        'cart-icon',
        'header',
        'mini-cart',
        'cart-notification',
        'cart-form',
      ].join(',');

      var response = await fetch('/?sections=' + sectionIds, {
        cache: 'no-store',
      });

      if (!response.ok) return;

      var sections = await response.json();

      // Update each section in the DOM
      for (var sectionId in sections) {
        if (sections.hasOwnProperty(sectionId)) {
          var html = sections[sectionId];
          var sectionElement = document.getElementById('shopify-section-' + sectionId);
          
          if (sectionElement && typeof html === 'string') {
            // Parse the new HTML and extract the section content
            var tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            var newSection = tempDiv.firstElementChild;

            if (newSection) {
              sectionElement.innerHTML = newSection.innerHTML;
            }
          }
        }
      }
    } catch (error) {
      // Section refresh is optional, don't throw
      console.log('[B2B Volume Discount] Section refresh skipped:', error);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateExistingCartItems);
  } else {
    updateExistingCartItems();
  }
})();
